import type { Invitation } from '@debtbot/shared';

/**
 * Повторный тап «Пригласить» без отправки не должен плодить приглашения: на сервере лимит
 * 10 неоткрытых, а вернуть старую ссылку сервер не может — токен хранится только хешем.
 * Поэтому последнюю ссылку помнит клиент (на время сессии Mini App).
 */
export const INVITE_REUSE_MS = 10 * 60_000;

const STORAGE_KEY = 'debtbot:last-invite';

export type CachedInvite = {
  invite: Invitation;
  /** Время клиента, а не created_at — не зависим от расхождения часов с сервером. */
  savedAt: number;
  /** Новая связь — вероятно, ссылку уже приняли; одноразовую отправлять второму нельзя. */
  connections: number;
};

export function reusableInvite(
  cached: CachedInvite | null,
  connections: number,
  now: number = Date.now(),
): Invitation | null {
  if (!cached || cached.connections !== connections) return null;
  // Черновик привязан к конкретной записи — такую ссылку второй раз не отправляют
  if (cached.invite.draft) return null;
  if (now - cached.savedAt >= INVITE_REUSE_MS) return null;
  if (Date.parse(cached.invite.expires_at) <= now) return null;
  return cached.invite;
}

export function loadCachedInvite(): CachedInvite | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CachedInvite) : null;
  } catch {
    return null;
  }
}

export function saveCachedInvite(invite: Invitation, connections: number): void {
  try {
    const value: CachedInvite = { invite, savedAt: Date.now(), connections };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Хранилище недоступно — просто создадим новую ссылку в следующий раз
  }
}

/**
 * Все ссылки, созданные в этой сессии Mini App, по id приглашения — для «Отправить ещё раз»
 * в списке ожидающих. После перезапуска Mini App ссылок нет: тогда создаётся новая,
 * а старая остаётся действительной (отозвать её по этому пути нельзя).
 */
const LINKS_KEY = 'debtbot:invite-links';

function loadLinks(): Record<string, Invitation> {
  try {
    const raw = sessionStorage.getItem(LINKS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, Invitation>) : {};
  } catch {
    return {};
  }
}

export function rememberInvite(invite: Invitation): void {
  try {
    sessionStorage.setItem(LINKS_KEY, JSON.stringify({ ...loadLinks(), [invite.id]: invite }));
  } catch {
    // Хранилище недоступно — для повторной отправки создадим новую ссылку
  }
}

/** Ссылка по id, если она ещё жива. */
export function recallInvite(id: string, now: number = Date.now()): Invitation | null {
  const invite = loadLinks()[id];
  if (!invite || Date.parse(invite.expires_at) <= now) return null;
  return invite;
}
